// Eskom PARCC — legacy checklist-blob parser (pure).
//
// Imported / legacy working papers have no normalised CES rows: the whole
// compliance checklist was captured as ONE text blob in `updatedFindings` /
// `workingPaperNotes`. The blob is a sequence of numbered items separated by
// blank lines —
//
//   3. 1.4 Inspect Conflict Declaration status (DoI & NDA)
//   Result: Non-Compliant - Internal Policies
//   Finding: no DoI on file
//   Action: follow up with buyer
//
// — optionally followed by free-text commentary (a closing paragraph, "Folder
// empty.", etc.). This parser turns that wall-of-text back into structured rows
// so the document export renders a real checklist for legacy papers, with any
// non-item prose preserved as `commentary`. Pure — no DOM, no XLSX.

/** One parsed checklist item. Empty strings (never undefined) for absent fields. */
export interface ParsedChecklistItem {
  /** The leading item number as written (e.g. "3"); gaps are preserved, never filled. */
  no: string;
  /** The criterion text after the number — usually a sub-code + description ("1.4 Inspect …"). */
  criterion: string;
  result: string;
  finding: string;
  action: string;
}

/** The structured form of a checklist blob: its items plus any trailing/free-text prose. */
export interface ParsedChecklist {
  items: ParsedChecklistItem[];
  commentary: string;
}

/** A line that opens a checklist item: "<n>. <criterion…>". Captures the number + the rest. */
const ITEM_START = /^\s*(\d+)\.\s+(.*\S)\s*$/;
/** A "Label: value" line (Result / Finding / Action), case- and whitespace-insensitive. */
const LABEL_LINE = /^\s*(result|finding|action)\s*:\s*(.*)$/i;

/** Split a blob into blocks separated by one or more blank lines; trims outer whitespace. */
function toBlocks(blob: string): string[][] {
  const blocks: string[][] = [];
  let current: string[] = [];
  for (const raw of blob.replace(/\r\n/g, '\n').split('\n')) {
    if (raw.trim() === '') {
      if (current.length) blocks.push(current);
      current = [];
    } else {
      current.push(raw);
    }
  }
  if (current.length) blocks.push(current);
  return blocks;
}

/**
 * Parse one item block. Line 0 is the "<n>. <criterion>" opener; subsequent lines
 * either set a field via a `Label:` prefix or continue the current field (so a
 * wrapped Finding still reads whole). Continuation before any label appends to the
 * criterion.
 */
function parseItem(no: string, firstCriterion: string, rest: string[]): ParsedChecklistItem {
  const item: ParsedChecklistItem = { no, criterion: firstCriterion, result: '', finding: '', action: '' };
  let field: 'criterion' | 'result' | 'finding' | 'action' = 'criterion';
  for (const line of rest) {
    const label = LABEL_LINE.exec(line);
    if (label) {
      field = label[1].toLowerCase() as typeof field;
      item[field] = label[2].trim();
    } else {
      const cont = line.trim();
      item[field] = item[field] ? `${item[field]} ${cont}` : cont;
    }
  }
  return item;
}

/**
 * Parse a legacy checklist blob into structured items + commentary. A block whose
 * first line opens with "<n>. " is a checklist item; any other block is free-text
 * commentary (joined back with newlines, in order). Blank / absent input yields
 * empty results. Skipped item numbers are preserved exactly — gaps are never filled.
 */
export function parseChecklistBlob(blob: string | null | undefined): ParsedChecklist {
  const items: ParsedChecklistItem[] = [];
  const commentary: string[] = [];
  if (!blob || blob.trim() === '') return { items, commentary: '' };

  for (const block of toBlocks(blob)) {
    const start = ITEM_START.exec(block[0]);
    if (start) {
      items.push(parseItem(start[1], start[2].trim(), block.slice(1)));
    } else {
      commentary.push(block.join('\n'));
    }
  }
  return { items, commentary: commentary.join('\n\n') };
}
